import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Truck,
  Package,
  Lightbulb,
  Clock,
  ArrowLeft,
  Building,
  CircleSlash,
  Calendar,
  FileText,
  ListTodo,
  PackageCheck,
  ClipboardCheck,
  Inbox,
  BedDouble,
  BarChart3,
  Stethoscope,
} from 'lucide-react';

import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fetchLaundryById } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext.jsx';

const LaundryDashboard = () => {
  const { idLavanderia } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission, MODULES } = useAuth();
  const [laundry, setLaundry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLaundry = async () => {
      try {
        setIsLoading(true);
        const data = await fetchLaundryById(idLavanderia);
        setLaundry(data);
      } catch (error) {
        console.error('Error cargando lavandería:', error);
        toast({
          title: 'Error',
          description: 'No se pudo cargar la información de la lavandería',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (idLavanderia) {
      loadLaundry();
    }
  }, [idLavanderia, toast]);

  const menuItems = [
    {
      id: 'recepcion',
      title: 'Recepción Ropa Sucia',
      description: 'Registrar entrada de ropa sucia para procesar',
      icon: <Truck className="h-8 w-8 text-primary" />,
      path: `/centros-lavado/${idLavanderia}/recepcion`,
      iconBg: 'bg-primary/15',
      borderColor: 'hover:border-primary/50',
      color: 'bg-primary/10',
      isReady: true,
    },
    {
      id: 'ciclos',
      title: 'Ciclos de lavado',
      description: 'Gestionar los ciclos de lavado',
      icon: <CircleSlash className="h-8 w-8 text-red-500 dark:text-red-400" />,
      path: `/centros-lavado/${idLavanderia}/ciclos`,
      iconBg: 'bg-red-500/15',
      borderColor: 'hover:border-red-500/50',
      color: 'bg-red-500/10',
      isReady: true,
    },
    {
      id: 'ciclos-secado',
      title: 'Ciclos de Secado',
      description: 'Gestionar los ciclos de secado',
      icon: <CircleSlash className="h-8 w-8 text-violet-500 dark:text-violet-400" />,
      path: `/centros-lavado/${idLavanderia}/ciclos-secado`,
      iconBg: 'bg-violet-500/15',
      borderColor: 'hover:border-violet-500/50',
      color: 'bg-violet-500/10',
      isReady: true,
    },
    {
      id: 'servicios',
      title: 'Servicios Públicos',
      description: 'Registrar consumos y facturación de servicios',
      icon: <Lightbulb className="h-8 w-8 text-orange-500 dark:text-orange-400" />,
      path: `/centros-lavado/${idLavanderia}/servicios`,
      iconBg: 'bg-orange-500/15',
      borderColor: 'hover:border-orange-500/50',
      color: 'bg-orange-500/10',
      isReady: true,
      // Solo visible para usuarios con permiso sobre el módulo
      // utility_services (admin + rol servicios_publicos).
      requiredModule: MODULES.UTILITY_SERVICES,
    },
    {
      id: 'horas',
      title: 'Entradas y Salidas de Personal',
      description: 'Registro de asistencia del personal',
      icon: <Clock className="h-8 w-8 text-blue-500 dark:text-blue-400" />,
      path: `/centros-lavado/${idLavanderia}/horas`,
      iconBg: 'bg-blue-500/15',
      borderColor: 'hover:border-blue-500/50',
      color: 'bg-blue-500/10',
      isReady: true,
    },
    {
      id: 'ausencias',
      title: 'Incapacidades y Permisos',
      description: 'Registrar incapacidades y permisos del personal del centro',
      icon: <Stethoscope className="h-8 w-8 text-rose-500 dark:text-rose-400" />,
      path: `/centros-lavado/${idLavanderia}/ausencias`,
      iconBg: 'bg-rose-500/15',
      borderColor: 'hover:border-rose-500/50',
      color: 'bg-rose-500/10',
      isReady: true,
      // Es gestión del mismo personal: aparece donde ya se registran entradas y salidas,
      // aunque no se haya habilitado aparte en las funcionalidades del centro.
      habilitadoCon: 'horas',
    },
    {
      id: 'turnos',
      title: 'Gestion de Turnos',
      description: 'Gestionar turnos de centros de lavado',
      icon: <Calendar className="h-8 w-8 text-cyan-500 dark:text-cyan-400" />,
      path: `/centros-lavado/${idLavanderia}/turnos`,
      iconBg: 'bg-cyan-500/15',
      borderColor: 'hover:border-cyan-500/50',
      color: 'bg-cyan-500/10',
      isReady: true,
    },
    {
      id: 'inventarios',
      title: 'Inventarios de Productos',
      description: 'Gestionar inventario de productos y materiales',
      icon: <Package className="h-8 w-8 text-yellow-500 dark:text-yellow-400" />,
      path: `/centros-lavado/${idLavanderia}/inventarios`,
      iconBg: 'bg-yellow-500/15',
      borderColor: 'hover:border-yellow-500/50',
      color: 'bg-yellow-500/10',
      isReady: true,
    },
    {
      id: 'inventario-clientes',
      title: 'Inventario por Cliente',
      description: 'Gestionar inventario de prendas por cliente',
      icon: <Package className="h-8 w-8 text-indigo-500 dark:text-indigo-400" />,
      path: `/centros-lavado/${idLavanderia}/inventario-clientes`,
      iconBg: 'bg-indigo-500/15',
      borderColor: 'hover:border-indigo-500/50',
      color: 'bg-indigo-500/10',
      isReady: true,
    },
    {
      id: 'alistamientos',
      title: 'Alistamientos',
      description: 'Alistar prendas por cliente para entrega',
      icon: <PackageCheck className="h-8 w-8 text-purple-500 dark:text-purple-400" />,
      path: `/centros-lavado/${idLavanderia}/alistamientos`,
      iconBg: 'bg-purple-500/15',
      borderColor: 'hover:border-purple-500/50',
      color: 'bg-purple-500/10',
      isReady: true,
    },
  /*   {
      id: 'rutas',
      title: 'Rutas de Entrega',
      description: 'Gestionar entregas y recolecciones de ropa por cliente',
      icon: <MapPin className="h-8 w-8 text-teal-500 dark:text-teal-400" />,
      path: `/centros-lavado/${idLavanderia}/rutas`,
      iconBg: 'bg-teal-500/15',
      borderColor: 'hover:border-teal-500/50',
      color: 'bg-teal-500/10',
      isReady: true,
    }, */
    {
      id: 'recepcion-ropa-clientes',
      title: 'Recibir Ropa Clientes',
      description: 'Registrar recepción de prendas de clientes',
      icon: <Inbox className="h-8 w-8 text-emerald-500 dark:text-emerald-400" />,
      path: `/centros-lavado/${idLavanderia}/recepcion-ropa-clientes`,
      iconBg: 'bg-emerald-500/15',
      borderColor: 'hover:border-emerald-500/50',
      color: 'bg-emerald-500/10',
      isReady: true,
    },
    {
      id: 'gestion-entregas',
      title: 'Gestión de Entregas',
      description: 'Ver y reasignar entregas que están en camino a conductores',
      icon: <Truck className="h-8 w-8 text-orange-500 dark:text-orange-400" />,
      path: `/centros-lavado/${idLavanderia}/gestion-entregas`,
      iconBg: 'bg-orange-500/15',
      borderColor: 'hover:border-orange-500/50',
      color: 'bg-orange-500/10',
      isReady: true,
    },
    {
      id: 'verificacion-ropa',
      title: 'Verificación Ropa Sucia',
      description: 'Verificar prendas sucias recolectadas antes de afectar inventario',
      icon: <ClipboardCheck className="h-8 w-8 text-amber-500 dark:text-amber-400" />,
      path: `/centros-lavado/${idLavanderia}/verificacion-ropa`,
      iconBg: 'bg-amber-500/15',
      borderColor: 'hover:border-amber-500/50',
      color: 'bg-amber-500/10',
      isReady: true,
    },
    {
      id: 'productos',
      title: 'Lista de productos y precios',
      description: 'Gestionar lista de productos y precios',
      icon: <ListTodo className="h-8 w-8 text-green-500 dark:text-green-400" />,
      path: `/centros-lavado/${idLavanderia}/productos`,
      iconBg: 'bg-green-500/15',
      borderColor: 'hover:border-green-500/50',
      color: 'bg-green-500/10',
      isReady: true,
      requiredModule: MODULES.PRODUCTS,
    },
    {
      id: 'documentacion',
      title: 'Documentación',
      description: 'Gestionar documentación de centros de lavado',
      icon: <FileText className="h-8 w-8 text-gray-500 dark:text-gray-400" />,
      path: `/centros-lavado/${idLavanderia}/documentacion`,
      iconBg: 'bg-gray-500/15',
      borderColor: 'hover:border-gray-500/50',
      color: 'bg-gray-500/10',
      isReady: true,
    },
    {
      id: 'ocupacion-clientes',
      title: 'Ocupación de Clientes',
      description: 'Registrar la ocupación diaria de habitaciones por cliente',
      icon: <BedDouble className="h-8 w-8 text-teal-500 dark:text-teal-400" />,
      path: `/centros-lavado/${idLavanderia}/ocupacion-clientes`,
      iconBg: 'bg-teal-500/15',
      borderColor: 'hover:border-teal-500/50',
      color: 'bg-teal-500/10',
      isReady: true,
    },
    {
      id: 'reporte-supervisor',
      title: 'Reporte de Supervisor',
      description: 'Rendimiento del centro y análisis de productividad por hora autorizada',
      icon: <BarChart3 className="h-8 w-8 text-amber-500 dark:text-amber-400" />,
      path: `/centros-lavado/${idLavanderia}/reporte-supervisor`,
      iconBg: 'bg-amber-500/15',
      borderColor: 'hover:border-amber-500/50',
      color: 'bg-amber-500/10',
      isReady: true,
      alwaysShow: true,
    },
  ];

  const dashboardSections = [
    {
      id: 'operacion',
      title: 'Operaciones de Lavadería',
      description: 'Control diario de recepción, personal y alistamientos',
      itemIds: ['recepcion', 'horas', 'ausencias', 'alistamientos', 'recepcion-ropa-clientes'],
    },
    {
      id: 'logistica',
      title: 'Logística',
      description: 'Coordinación de rutas, recolección y verificación',
      itemIds: ['verificacion-ropa', 'gestion-entregas'],
    },
    {
      id: 'registros',
      title: 'Registros diarios',
      description: 'Seguimiento de ciclos, servicios e inventarios',
      itemIds: ['ciclos', 'ciclos-secado', 'servicios', 'inventarios', 'ocupacion-clientes'],
    },
    {
      id: 'analisis',
      title: 'Análisis y reportes',
      description: 'Indicadores de rendimiento del centro de lavado',
      itemIds: ['reporte-supervisor'],
    },
  ];

  const filteredMenuItems = menuItems.filter((item) => {
    // Las cards operativas del centro (recepción, ciclos, horas, etc.)
    // no declaran requiredModule; se consideran parte del módulo general
    // WORKPLACES. Así, un usuario que solo tiene utility_services (rol
    // servicios_publicos) únicamente ve la card de Servicios Públicos.
    const effectiveModule = item.requiredModule || MODULES.WORKPLACES;
    if (!hasPermission(effectiveModule)) {
      return false;
    }
    // Ítems marcados alwaysShow (p.ej. reportes) no dependen de las
    // funcionalidades configuradas del centro.
    if (item.alwaysShow) {
      return true;
    }
    // Si no hay funcionalidades configuradas, mostrar todas
    if (!laundry?.funcionalidadesHabilitadas || laundry.funcionalidadesHabilitadas.length === 0) {
      return true;
    }
    // Mostrar solo las funcionalidades habilitadas
    return (
      laundry.funcionalidadesHabilitadas.includes(item.id) ||
      (!!item.habilitadoCon && laundry.funcionalidadesHabilitadas.includes(item.habilitadoCon))
    );
  });

  const sectionsWithItems = dashboardSections
    .map((section) => ({
      ...section,
      items: section.itemIds
        .map((id) => filteredMenuItems.find((item) => item.id === id))
        .filter(Boolean),
    }))
    .filter((section) => section.items.length > 0);

  const assignedSectionItemIds = new Set(sectionsWithItems.flatMap((section) => section.items.map((item) => item.id)));
  const administrativeItems = filteredMenuItems.filter((item) => !assignedSectionItemIds.has(item.id));

  const handleMenuClick = (item) => {
    if (!item.isReady) {
      toast({
        title: 'En desarrollo',
        description: 'Esta funcionalidad estará disponible próximamente',
      });
      return;
    }
    if (item.requiredModule && !hasPermission(item.requiredModule)) {
      toast({
        title: 'Permiso denegado',
        description: 'No tienes permisos para acceder a este módulo.',
        variant: 'destructive',
      });
      return;
    }

    navigate(item.path);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="p-4 md:p-4 lg:p-4">


      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-border/50">
          <div className="flex items-center gap-4">
            <div className="">
              <Button asChild variant="outline">
                <Link to="/centros-lavado"><ArrowLeft className="mr-2 h-4 w-4" /> Volver</Link>
              </Button>
            </div>
            <div className="bg-primary/15 p-2.5 rounded-lg">
              <Building className="h-7 w-7 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">{isLoading ? 'Cargando...' : laundry?.nombre || 'Centro de Lavado'}</CardTitle>
              <CardDescription className="mt-1">

              </CardDescription>
              {!isLoading && laundry && (
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                  {laundry.direccion && (
                    <span className="flex items-center gap-1">
                      <Building className="h-3 w-3" />
                      {laundry.direccion}
                    </span>
                  )}
                  {laundry.telefono && (
                    <span>📞 {laundry.telefono}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-muted/40 rounded-lg border border-border h-32 animate-pulse" />
              ))}
            </div>
          ) : filteredMenuItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg mb-2">No hay funcionalidades configuradas para este centro de lavado</p>
              <p className="text-sm text-muted-foreground">Contacta al administrador para configurar las funcionalidades disponibles</p>
            </div>
          ) : (
            <div className="space-y-8">
              {sectionsWithItems.map((section) => (
                <div key={section.id}>
                  <div className="flex items-center gap-3 mb-4">
                    <div>
                      <h2 className="text-lg font-semibold">{section.title}</h2>
                      <p className="text-xs text-muted-foreground">{section.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {section.items.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleMenuClick(item)}
                        className={`${item.color} p-6 rounded-lg border border-border shadow-sm transition-all duration-200 ${(!item.isReady || (item.requiredModule && !hasPermission(item.requiredModule))) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-md ' + item.borderColor}`}
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className={`p-2.5 rounded-lg ${item.iconBg}`}>
                            {item.icon}
                          </div>
                          <h3 className="text-base font-semibold mt-3">{item.title}</h3>
                          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{item.description}</p>
                          {!item.isReady && (
                            <span className="mt-2 px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                              Próximamente
                            </span>
                          )}
                          {(item.requiredModule && !hasPermission(item.requiredModule)) && (
                            <span className="mt-2 px-2 py-0.5 text-xs rounded-full bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800">
                              Sin permiso
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}

              {administrativeItems.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div>
                      <h2 className="text-lg font-semibold">Administración</h2>
                      <p className="text-xs text-muted-foreground">Herramientas de gestión y configuración</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {administrativeItems.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleMenuClick(item)}
                        className={`${item.color} p-6 rounded-lg border border-border shadow-sm transition-all duration-200 ${(!item.isReady || (item.requiredModule && !hasPermission(item.requiredModule))) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-md ' + item.borderColor}`}
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className={`p-2.5 rounded-lg ${item.iconBg}`}>
                            {item.icon}
                          </div>
                          <h3 className="text-base font-semibold mt-3">{item.title}</h3>
                          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{item.description}</p>
                          {!item.isReady && (
                            <span className="mt-2 px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                              Próximamente
                            </span>
                          )}
                          {(item.requiredModule && !hasPermission(item.requiredModule)) && (
                            <span className="mt-2 px-2 py-0.5 text-xs rounded-full bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800">
                              Sin permiso
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default LaundryDashboard;
